/**
 * Shim peste `flowbite-react`.
 *
 * Întreaga aplicație importă `Modal` (și restul componentelor) din
 * `flowbite-react`. Modalul nativ din flowbite-react@0.9 NU se închide la tasta
 * ESC decât dacă i se pasează prop-ul `dismissible` — care activează ÎNSĂ și
 * închiderea la click în afară (risc de pierdere de date la modalele cu
 * formulare).
 *
 * Aici re-exportăm tot din pachetul original și înlocuim DOAR `Modal` cu un
 * wrapper care adaugă închiderea la ESC (fără click-outside, în afară de `closeOnBackdropClick`).
 * `onEscape` (opțional) înlocuiește `onClose` doar pentru ESC — ex. o confirmare.
 * `closeOnBackdropClick` (opțional) închide modalul la click pe fundalul întunecat — doar pe fundal,
 * nu pe meniurile din portal (select-uri, calendare), cum ar face `dismissible`.
 *
 * Vite redirecționează importul exact `flowbite-react` către acest fișier
 * (vezi alias-urile din `vite.config.js`). Pachetul real este accesat prin
 * alias-ul `@flowbite-original`, ca să evităm recursia.
 */
import React, { forwardRef, useEffect, useRef } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Modal as BaseModal } from '@flowbite-original';

// Re-exportă tot ce oferă pachetul original (Button, Label, Dropdown, etc.).
// eslint-disable-next-line import/no-unresolved
export * from '@flowbite-original';

type BaseModalProps = React.ComponentProps<typeof BaseModal> & {
  /** Ce face ESC (implicit `onClose`) — ex. cere confirmare înainte de închidere. */
  onEscape?: () => void;
  /** Click pe fundal (în afara ferestrei) → `onClose`. */
  closeOnBackdropClick?: boolean;
};

/** Fundalul modalului (FloatingOverlay din Flowbite) — doar el închide la click în afară. */
const isBackdrop = (e: React.MouseEvent) => (e.target as HTMLElement | null)?.getAttribute?.('data-testid') === 'modal-overlay';

/**
 * Stivă globală de modale deschise, în ordinea deschiderii. La ESC închidem doar
 * modalul din vârf (cel mai recent deschis), ca să nu închidem accidental și
 * modalele de dedesubt când sunt stivuite. Un modal intră în stivă când se
 * deschide și nu se mută la re-randare (altfel cel de dedesubt ar urca în vârf).
 */
const modalStack: Array<{ escape: () => void }> = [];
let listenerAttached = false;

/**
 * ESC aparține întâi controlului cu focus care are o listă deschisă (select, combobox,
 * sugestii, calendar, popover / meniu Radix — ex. căutarea din viewer-ul PDF): el își
 * închide lista, modalul rămâne deschis.
 */
const escapeBelongsToControl = (): boolean => {
  const active = document.activeElement as HTMLElement | null;
  if (active?.getAttribute('aria-expanded') === 'true') return true;
  if (active?.closest('[aria-expanded="true"]')) return true;
  if (document.querySelector('[data-radix-popper-content-wrapper]')) return true;
  return !!document.querySelector('.react-datepicker-popper');
};

const handleGlobalKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' && event.key !== 'Esc') return;
  const top = modalStack[modalStack.length - 1];
  if (!top || escapeBelongsToControl()) return;
  event.preventDefault();
  event.stopPropagation();
  top.escape();
};

const EscModalComponent = forwardRef<HTMLDivElement, BaseModalProps>((props, ref) => {
  const { show, onClose, onEscape, closeOnBackdropClick, ...rest } = props;
  // Click în afară = apăsat ȘI eliberat pe fundal (o selecție de text începută în fereastră nu închide).
  const pressedOnBackdrop = useRef(false);
  const backdropProps = closeOnBackdropClick
    ? {
        onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
          pressedOnBackdrop.current = isBackdrop(e);
          rest.onMouseDown?.(e);
        },
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
          if (pressedOnBackdrop.current && isBackdrop(e)) onClose?.();
          pressedOnBackdrop.current = false;
          rest.onClick?.(e);
        },
      }
    : {};
  // Ultimele callback-uri, citite la ESC — intrarea din stivă rămâne aceeași cât modalul e deschis.
  const escapeRef = useRef<(() => void) | undefined>(onEscape || onClose);
  escapeRef.current = onEscape || onClose;

  useEffect(() => {
    if (!show) return undefined;
    const entry = { escape: () => escapeRef.current?.() };
    modalStack.push(entry);

    if (!listenerAttached) {
      document.addEventListener('keydown', handleGlobalKeyDown, true);
      listenerAttached = true;
    }

    return () => {
      const index = modalStack.indexOf(entry);
      if (index !== -1) modalStack.splice(index, 1);
      if (modalStack.length === 0 && listenerAttached) {
        document.removeEventListener('keydown', handleGlobalKeyDown, true);
        listenerAttached = false;
      }
    };
  }, [show]);

  return <BaseModal ref={ref} show={show} onClose={onClose} {...rest} {...backdropProps} />;
});

EscModalComponent.displayName = 'Modal';

// Păstrăm sub-componentele (Modal.Header / Modal.Body / Modal.Footer).
export const Modal = Object.assign(EscModalComponent, {
  Header: (BaseModal as any).Header,
  Body: (BaseModal as any).Body,
  Footer: (BaseModal as any).Footer,
});
